import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import {
  useManifestUrl,
  useRemoveWorkspace,
  useSlackConfig,
  useStoreCredentials,
} from "@/lib/hooks/useSlackIntegration";
import {
  AlertTriangle,
  Check,
  CheckCircle,
  Copy,
  ExternalLink,
  Loader2,
  Slack,
  Trash2,
} from "lucide-react";
import { useCallback, useRef, useState } from "react";

interface SlackIntegrationProps {
  agentName: string;
}

export function SlackIntegration({ agentName }: SlackIntegrationProps) {
  const [credentials, setCredentials] = useState({
    clientId: "",
    clientSecret: "",
    signingSecret: "",
  });
  const [copied, setCopied] = useState(false);
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const [workspaceToRemove, setWorkspaceToRemove] = useState<{
    teamId: string;
    teamName: string;
  } | null>(null);

  // Autosave state
  const [saveStatus, setSaveStatus] = useState<
    "idle" | "saving" | "saved" | "error"
  >("idle");
  const [editingField, setEditingField] = useState<string | null>(null);
  const autosaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const { data: slackConfigResponse, isLoading: configLoading } =
    useSlackConfig(agentName);
  const { data: manifestUrlResponse, isLoading: manifestLoading } =
    useManifestUrl(agentName);
  const storeCredentials = useStoreCredentials(agentName);
  const removeWorkspace = useRemoveWorkspace(agentName);

  const slackConfig = slackConfigResponse?.data;
  const manifestUrl = manifestUrlResponse?.data?.manifestUrl;
  const publicDomain = manifestUrlResponse?.data?.publicDomain;

  // Debounced autosave function
  const debouncedAutoSave = useCallback(
    (newCredentials: {
      clientId: string;
      clientSecret: string;
      signingSecret: string;
    }) => {
      if (autosaveTimeoutRef.current) {
        clearTimeout(autosaveTimeoutRef.current);
      }

      autosaveTimeoutRef.current = setTimeout(async () => {
        // Only save if all three fields are filled
        if (
          !newCredentials.clientId ||
          !newCredentials.clientSecret ||
          !newCredentials.signingSecret
        ) {
          return;
        }

        setSaveStatus("saving");

        try {
          await storeCredentials.mutateAsync(newCredentials);
          setSaveStatus("saved");
          setTimeout(() => {
            setSaveStatus("idle");
            setEditingField(null);
          }, 4000);
        } catch (error) {
          console.error("❌ Save operation failed with error:", error);
          setSaveStatus("error");
          setTimeout(() => {
            setSaveStatus("idle");
            setEditingField(null);
          }, 5000);
        }
      }, 1000); // Wait 1 second after user stops typing
    },
    [storeCredentials],
  );

  // Save status indicator component
  const SaveStatusIndicator = useCallback(
    ({ fieldName }: { fieldName: string }) => {
      if (editingField !== fieldName) {
        return null;
      }

      switch (saveStatus) {
        case "saving":
          return (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-3 w-3 animate-spin" />
              Saving...
            </div>
          );
        case "saved":
          return (
            <div className="flex items-center gap-2 text-sm text-green-600">
              <CheckCircle className="h-3 w-3" />
              Saved
            </div>
          );
        case "error":
          return (
            <div className="flex items-center gap-2 text-sm text-red-600">
              <AlertTriangle className="h-3 w-3" />
              Save failed
            </div>
          );
        default:
          return null;
      }
    },
    [editingField, saveStatus],
  );

  const handleCopyUrl = async (url: string) => {
    await navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleInstallToWorkspace = () => {
    if (slackConfig?.clientId) {
      const installUrl = generateInstallUrl(slackConfig.clientId);
      window.open(installUrl, "_blank");
    }
  };

  const handleRemoveWorkspace = async (teamId: string) => {
    try {
      await removeWorkspace.mutateAsync(teamId);
      setRemoveDialogOpen(false);
      setWorkspaceToRemove(null);
    } catch (error) {
      console.error("Failed to remove workspace:", error);
    }
  };

  const openRemoveDialog = (teamId: string, teamName: string) => {
    setWorkspaceToRemove({ teamId, teamName });
    setRemoveDialogOpen(true);
  };

  // Generate install URL using public domain
  const generateInstallUrl = (clientId: string) => {
    const domain = publicDomain || window.location.origin;
    const scopes = encodeURIComponent(
      "app_mentions:read,channels:history,chat:write,files:read,files:write,groups:history,im:history,im:read,im:write,mpim:history,reactions:read,reactions:write,team:read,users:read,channels:read,groups:read,mpim:read",
    );
    const redirectUri = encodeURIComponent(
      `${domain}/api/slack/agent/${agentName}/install/callback`,
    );
    return `https://slack.com/oauth/v2/authorize?client_id=${clientId}&scope=${scopes}&redirect_uri=${redirectUri}`;
  };

  if (configLoading || manifestLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Slack className="h-5 w-5" />
            Slack Integration
          </CardTitle>
          <CardDescription>Loading Slack configuration...</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  // If already configured, show existing configuration
  if (slackConfig) {
    return (
      <>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Slack className="h-5 w-5" />
              Slack Integration
            </CardTitle>
            <CardDescription>
              Your agent is configured for Slack integration
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">App ID:</span>
                <code className="text-xs bg-muted px-2 py-1 rounded">
                  {slackConfig?.appId || "Not yet installed"}
                </code>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Client ID:</span>
                <code className="text-xs bg-muted px-2 py-1 rounded">
                  {slackConfig?.clientId}
                </code>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Created:</span>
                <span className="text-xs text-muted-foreground">
                  {slackConfig?.createdAt
                    ? new Date(slackConfig.createdAt).toLocaleDateString()
                    : "Unknown"}
                </span>
              </div>
            </div>

            <Separator />

            <div className="space-y-3">
              <h4 className="text-sm font-medium">Workspace Installations</h4>

              {!slackConfig?.installedWorkspaces?.length ? (
                <div className="space-y-2">
                  <p className="text-sm text-muted-foreground">
                    No workspaces installed yet
                  </p>
                  <Button
                    onClick={() => {
                      const installUrl = generateInstallUrl(
                        slackConfig.clientId,
                      );
                      window.open(installUrl, "_blank");
                    }}
                    variant="outline"
                    className="w-full"
                  >
                    Install to Workspace
                  </Button>
                </div>
              ) : (
                <div className="space-y-2">
                  {slackConfig?.installedWorkspaces?.map((workspace) => (
                    <div
                      key={workspace.teamId}
                      className="flex items-center justify-between p-2 bg-muted rounded-lg"
                    >
                      <div className="flex items-center gap-2">
                        <div className="h-2 w-2 bg-green-500 rounded-full" />
                        <span className="text-sm font-medium">
                          {workspace.teamName}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-muted-foreground">
                          {new Date(workspace.installedAt).toLocaleDateString()}
                        </span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() =>
                            openRemoveDialog(
                              workspace.teamId,
                              workspace.teamName,
                            )
                          }
                          disabled={removeWorkspace.isPending}
                          className="h-6 w-6 p-0 text-muted-foreground hover:text-destructive"
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  ))}

                  <Button
                    onClick={() => {
                      console.log(
                        "Install to Another Workspace button clicked",
                      );
                      if (slackConfig?.clientId) {
                        const installUrl = generateInstallUrl(
                          slackConfig.clientId,
                        );
                        window.open(installUrl, "_blank");
                      } else {
                        console.log(
                          "No clientId found in install to another workspace section",
                        );
                      }
                    }}
                    variant="outline"
                    size="sm"
                    className="w-full"
                  >
                    Install to Another Workspace
                  </Button>
                </div>
              )}
            </div>

            {(slackConfig?.installedWorkspaces?.length || 0) > 0 && (
              <>
                <Separator />
                <div className="space-y-2">
                  <h4 className="text-sm font-medium">Usage</h4>
                  <div className="text-sm text-muted-foreground space-y-1">
                    <p>
                      • Mention <code>@{slackConfig?.botName}</code> in any
                      channel
                    </p>
                    <p>• Send direct messages to the bot</p>
                    <p>
                      • The agent will respond using your configured model and
                      tools
                    </p>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Remove Workspace Dialog */}
        <Dialog open={removeDialogOpen} onOpenChange={setRemoveDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Remove Workspace</DialogTitle>
              <DialogDescription>
                Are you sure you want to remove "{workspaceToRemove?.teamName}"
                from your Slack integration? This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setRemoveDialogOpen(false)}
                disabled={removeWorkspace.isPending}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={async () => {
                  if (workspaceToRemove) {
                    await handleRemoveWorkspace(workspaceToRemove.teamId);
                  }
                }}
                disabled={removeWorkspace.isPending}
              >
                {removeWorkspace.isPending ? "Removing..." : "Remove"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  // Setup flow
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Slack className="h-5 w-5" />
          Slack Integration Setup
        </CardTitle>
        <CardDescription>
          Create a dedicated Slack app for your agent with minimal setup
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-6">
          {/* Step 1: Create Slack App */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <div className="flex h-6 w-6 items-center justify-center rounded-full bg-muted text-xs font-medium">
                1
              </div>
              <h3 className="text-sm font-medium">Create Slack App</h3>
            </div>

            <p className="text-sm text-muted-foreground ml-8">
              Click the URL below to create your Slack app, then copy the
              credentials back here.
            </p>

            <div className="ml-8 space-y-3">
              {manifestUrl ? (
                <div className="flex items-center gap-2 p-2 bg-muted rounded border">
                  <code className="text-xs flex-1 whitespace-nowrap overflow-x-auto">
                    {manifestUrl}
                  </code>
                  <div className="flex gap-1 flex-shrink-0">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleCopyUrl(manifestUrl)}
                    >
                      {copied ? (
                        <Check className="h-3 w-3" />
                      ) : (
                        <Copy className="h-3 w-3" />
                      )}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => window.open(manifestUrl, "_blank")}
                    >
                      <ExternalLink className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="text-sm text-muted-foreground">
                  Loading manifest URL...
                </div>
              )}

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="clientId">Client ID</Label>
                  <SaveStatusIndicator fieldName="clientId" />
                </div>
                <Input
                  id="clientId"
                  placeholder="1234567890.1234567890123"
                  value={credentials.clientId}
                  onChange={(e) => {
                    const newCredentials = {
                      ...credentials,
                      clientId: e.target.value,
                    };
                    setCredentials(newCredentials);
                    setEditingField("clientId");
                    debouncedAutoSave(newCredentials);
                  }}
                />
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="clientSecret">Client Secret</Label>
                  <SaveStatusIndicator fieldName="clientSecret" />
                </div>
                <Input
                  id="clientSecret"
                  placeholder="abcdef1234567890abcdef1234567890"
                  value={credentials.clientSecret}
                  onChange={(e) => {
                    const newCredentials = {
                      ...credentials,
                      clientSecret: e.target.value,
                    };
                    setCredentials(newCredentials);
                    setEditingField("clientSecret");
                    debouncedAutoSave(newCredentials);
                  }}
                />
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="signingSecret">Signing Secret</Label>
                  <SaveStatusIndicator fieldName="signingSecret" />
                </div>
                <Input
                  id="signingSecret"
                  placeholder="abcdef1234567890abcdef1234567890abcdef12"
                  value={credentials.signingSecret}
                  onChange={(e) => {
                    const newCredentials = {
                      ...credentials,
                      signingSecret: e.target.value,
                    };
                    setCredentials(newCredentials);
                    setEditingField("signingSecret");
                    debouncedAutoSave(newCredentials);
                  }}
                />
              </div>

              {/* Auto-saved status message */}
              {credentials.clientId &&
                credentials.clientSecret &&
                credentials.signingSecret && (
                  <div className="text-sm text-muted-foreground">
                    💡 Credentials are automatically saved when all fields are
                    completed
                  </div>
                )}
            </div>
          </div>

          {/* Step 2: Install to Workspace */}
          {slackConfig && (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <div className="flex h-6 w-6 items-center justify-center rounded-full bg-muted text-xs font-medium">
                  2
                </div>
                <h3 className="text-sm font-medium">Install to Workspace</h3>
              </div>

              <p className="text-sm text-muted-foreground ml-8">
                Great! Your Slack app is configured. Now install it to a
                workspace to start using it.
              </p>

              <div className="ml-8">
                <Button onClick={handleInstallToWorkspace} className="w-full">
                  Install to Workspace
                </Button>
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
